/*
 * DATA_RIGHTS: HONEYWELL CONFIDENTIAL AND PROPRIETARY
 * THIS WORK CONTAINS VALUABLE CONFIDENTIAL AND PROPRIETARY
 * INFORMATION. DISCLOSURE, USE OR REPRODUCTION OUTSIDE OF
 * HONEYWELL INTERNATIONAL, INC. IS PROHIBITED EXCEPT AS
 * AUTHORIZED IN WRITING. THIS UNPUBLISHED WORK IS PROTECTED BY
 * THE LAWS OF THE UNITED STATES AND OTHER COUNTRIES. IN THE
 * EVENT OF PUBLICATION, THE FOLLOWING NOTICE SHALL APPLY:
 * Copyright (C) 2022 HONEYWELL INTERNATIONAL, INC., ALL RIGHTS RESERVED.
 *
 * Created by H144765 on 3/31/2022.
 */

import _ from 'lodash'
import {Component} from 'react'

export default class BaseClass {
    /**
     * Helper method for introducing public field
     *
     * @param field
     * @param value
     * @returns {BaseClass}
     */
    publicField(field, value) {
        if (field) {
            this[field] = value
        }
        return this
    }

    /**
     * Helper method for introducing private field with given fieldName in object represented by givn thisBinding with appropriate getter and setter
     * methods following Java conventions with few exceptions. When last (optional) parameter is provided, setter method sets actual value only when
     * argument passed to setter matches expected type. In case of fields of type FUNCTION generated getter method doesn't contain get prefix. For fields
     * of Boolean type generated getter name is prefixed with get rather than is.
     *
     * @param thisBinding Object in which will be introduced getter and setter methods.
     * @param fieldName Name of the field for which getter and setter methods should be introduced.
     * @param privateStorage Object holding actual values hidden to object represented by thisBinding.
     * @param typeChecker Optional parameter where can be used one of predefined BaseClass.FIELD_TYPE, custom one which should be instance of
     *          BaseClass.TypeChecker. If this argument is not provided or is not of expected type it's silently ignored. If one of BaseClass.FIELD_TYPE
     *          value is provided it may influence generated getter and setter name.
     */
    static privateField(thisBinding, fieldName, privateStorage, typeChecker) {
        if (_.isUndefined(thisBinding) || _.isUndefined(fieldName) || !_.isString(fieldName) || _.isUndefined(privateStorage)) {
            return
        }
        let field = _.trim(fieldName)
        if (_.indexOf(field, ' ') >= 0) { // contains spaces
            field = _.camelCase(field)
        }
        const setter = 'set' + _.upperFirst(field)
        const getter = (typeChecker === this.FIELD_TYPE.Function) ?
            field : ('get' + _.upperFirst(field))
        thisBinding[setter] = (arg) => {
            if (!typeChecker || (typeChecker instanceof this.TypeChecker && typeChecker.test(arg))) {
                privateStorage[field] = arg
            }
            // chaining support
            return thisBinding
        }
        thisBinding[getter] = () => {
            if (typeChecker === this.FIELD_TYPE.Function) {
                return privateStorage[field].apply(null, arguments)
            } else {
                return privateStorage[field]
            }
        }
    }
    static TypeChecker = class TypeChecker {
        constructor(fn) {
            if (!_.isFunction(fn)) {
                throw new Error('Expected type checker function returning true/false')
            }
            this.test = (arg) => fn(arg)
        }
    }
    static FIELD_TYPE = {
        String: new BaseClass.TypeChecker((arg) => _.isString(arg)),
        Function: new BaseClass.TypeChecker((arg) => _.isFunction(arg)),
        Number: new BaseClass.TypeChecker((arg) => _.isNumber(arg)),
        Boolean: new BaseClass.TypeChecker((arg) => _.isBoolean(arg)),
        ReactComponent: new BaseClass.TypeChecker((arg) => arg != null && arg instanceof Component),
    }
}